import { useState, useEffect, useCallback } from "react";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Minus, NotePencil, Package, Plus, Trash } from "@phosphor-icons/react";
import { publicAssetUrl } from "@/lib/api";
import { parseProductSizes, formatLineSizeDisplay, lineSizeLabel } from "@/lib/productSizes";

function productUnitLabel(p) {
  const unitStr = p?.unit != null ? String(p.unit).trim() : "";
  if (unitStr) return unitStr;
  const n = String(p?.name || "").toLowerCase();
  if (/\bsq\.?\s*ft\b|\bsqft\b|\bm²\b|\bsqm\b/i.test(n)) return "per sqft";
  if (/set|system|cuplock|bundle/i.test(n)) return "per sets";
  return "per pcs";
}

export default function BuyerSelectedMaterialsTable({
  selectedProducts,
  setSelectedProducts,
  testIdPrefix = "req",
  className,
  title = "Selected Materials",
  subtitle = "Scroll horizontally on small screens if needed.",
}) {
  const [quantityDrafts, setQuantityDrafts] = useState({});

  useEffect(() => {
    const ids = new Set(Object.keys(selectedProducts));
    setQuantityDrafts((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const id of Object.keys(next)) {
        if (!ids.has(id)) {
          delete next[id];
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [selectedProducts]);

  const clearQuantityDraft = useCallback((productId) => {
    setQuantityDrafts((prev) => {
      if (!(productId in prev)) return prev;
      const next = { ...prev };
      delete next[productId];
      return next;
    });
  }, []);

  const commitProductQuantity = useCallback(
    (productId, value) => {
      const n = Math.floor(Number(value));
      if (!Number.isFinite(n) || n < 1) return false;
      setSelectedProducts((prev) => {
        const row = prev[productId];
        if (!row || row.specType !== "quantity") return prev;
        return { ...prev, [productId]: { ...row, quantity: n } };
      });
      return true;
    },
    [setSelectedProducts]
  );

  const setLineSize = useCallback(
    (productId, size) => {
      setSelectedProducts((prev) => {
        const row = prev[productId];
        if (!row) return prev;
        return { ...prev, [productId]: { ...row, size } };
      });
    },
    [setSelectedProducts]
  );

  const setLineSpecType = useCallback(
    (productId, specType) => {
      clearQuantityDraft(productId);
      setSelectedProducts((prev) => {
        const row = prev[productId];
        if (!row) return prev;
        if (specType === "area") {
          return {
            ...prev,
            [productId]: {
              ...row,
              specType: "area",
              areaValue: row.areaValue ?? "",
              areaUnit: row.areaUnit || "sqft",
              quantity: Math.max(1, row.quantity || 1),
            },
          };
        }
        return {
          ...prev,
          [productId]: {
            ...row,
            specType: "quantity",
            quantity: Math.max(1, row.quantity || 1),
            areaValue: "",
            areaUnit: "sqft",
          },
        };
      });
    },
    [clearQuantityDraft, setSelectedProducts]
  );

  const applyQuantityDelta = useCallback(
    (productId, delta) => {
      clearQuantityDraft(productId);
      setSelectedProducts((prev) => {
        const row = prev[productId];
        if (!row || row.specType !== "quantity") return prev;
        const next = row.quantity + delta;
        if (next < 1) {
          toast.error("Quantity must be at least 1");
          return prev;
        }
        return { ...prev, [productId]: { ...row, quantity: next } };
      });
    },
    [clearQuantityDraft, setSelectedProducts]
  );

  const removeLine = useCallback(
    (lineKey) => {
      setSelectedProducts((prev) => {
        const next = { ...prev };
        delete next[lineKey];
        return next;
      });
    },
    [setSelectedProducts]
  );

  if (Object.keys(selectedProducts).length === 0) return null;

  return (
    <Card className={className ?? "rounded-xl border-neutral-200 shadow-sm overflow-hidden"} data-testid={`${testIdPrefix}-selected-materials`}>
      <CardContent className="p-0">
        <div className="px-5 py-4 border-b border-neutral-100 bg-white">
          <h2 className="text-base font-bold text-neutral-900">{title}</h2>
          <p className="text-xs text-neutral-500 mt-1">{subtitle}</p>
        </div>
        <div className="overflow-x-auto">
          <Table className="min-w-[760px]">
            <TableHeader>
              <TableRow className="border-neutral-200 bg-neutral-50/95 hover:bg-neutral-50/95">
                <TableHead className="pl-4 font-semibold text-neutral-700 min-w-[220px]">Product</TableHead>
                <TableHead className="w-[96px] font-semibold text-neutral-700 whitespace-nowrap text-center">Size</TableHead>
                <TableHead className="w-[108px] font-semibold text-neutral-700 whitespace-nowrap">Spec</TableHead>
                <TableHead className="font-semibold text-neutral-700 min-w-[200px]">Amount</TableHead>
                <TableHead className="w-28 font-semibold text-neutral-700 whitespace-nowrap">Unit</TableHead>
                <TableHead className="w-12 pr-4 text-right font-semibold text-neutral-700" scope="col">
                  <span className="sr-only">Remove</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {Object.entries(selectedProducts).map(([lineKey, p]) => {
                const spec = p.specType === "area" ? "area" : "quantity";
                const sizeOpts = parseProductSizes(p.sizes);
                const sizeLabel = lineSizeLabel(lineKey, p);
                const hasSize = Boolean(sizeLabel);
                return (
                  <TableRow
                    key={lineKey}
                    data-testid={`${testIdPrefix}-selected-item-${lineKey}`}
                    className="border-neutral-200 bg-white hover:bg-orange-50/30"
                  >
                    <TableCell className="pl-4 py-3">
                      <div className="flex items-center gap-3 min-w-0">
                        <div
                          className={`w-11 h-11 rounded-lg overflow-hidden shrink-0 flex items-center justify-center ${
                            p.isCustom ? "bg-violet-50 border border-violet-200" : "bg-neutral-100"
                          }`}
                        >
                          {p.isCustom ? (
                            <NotePencil size={22} weight="bold" className="text-violet-600" />
                          ) : p.image_url ? (
                            <img src={publicAssetUrl(p.image_url)} alt="" className="w-full h-full object-cover" />
                          ) : (
                            <Package size={22} className="text-neutral-300" />
                          )}
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-bold text-neutral-900 leading-snug truncate">{p.name}</p>
                          <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                            {p.isCustom ? (
                              <Badge
                                variant="secondary"
                                className="rounded-md text-[10px] bg-violet-100 text-violet-800 border-violet-200"
                              >
                                Custom
                              </Badge>
                            ) : null}
                            <span className="text-xs text-neutral-500 truncate">{p.category}</span>
                          </div>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="py-3 align-middle text-center">
                      {sizeOpts.length > 0 ? (
                        hasSize ? (
                          <span
                            className="inline-flex items-center justify-center min-w-[3rem] px-2.5 py-1 rounded-full text-xs font-bold bg-orange-50 text-[#FF7A00] border border-orange-200 whitespace-nowrap"
                            title={sizeLabel}
                          >
                            {sizeLabel}
                          </span>
                        ) : (
                          <Select
                            value="__none__"
                            onValueChange={(v) => setLineSize(lineKey, v === "__none__" ? "" : v)}
                          >
                            <SelectTrigger
                              className="h-9 w-[92px] mx-auto rounded-lg border-neutral-200 text-xs"
                              data-testid={`${testIdPrefix}-size-${lineKey}`}
                            >
                              <SelectValue placeholder="Size" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="__none__" className="text-neutral-500">
                                Select
                              </SelectItem>
                              {sizeOpts.map((s) => (
                                <SelectItem key={s} value={s}>
                                  {formatLineSizeDisplay(s, p.name)}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )
                      ) : (
                        <span className="text-xs text-neutral-400">—</span>
                      )}
                    </TableCell>
                    <TableCell className="py-3 align-middle">
                      <div className="inline-flex rounded-lg border border-neutral-200 overflow-hidden bg-white shadow-sm">
                        <button
                          type="button"
                          data-testid={`${testIdPrefix}-spec-qty-${lineKey}`}
                          className={`px-2 py-1.5 text-[10px] font-bold uppercase tracking-wide ${
                            spec === "quantity" ? "bg-[#FF7A00] text-white" : "bg-white text-neutral-600"
                          }`}
                          onClick={() => setLineSpecType(lineKey, "quantity")}
                        >
                          Qty
                        </button>
                        <button
                          type="button"
                          data-testid={`${testIdPrefix}-spec-area-${lineKey}`}
                          className={`px-2 py-1.5 text-[10px] font-bold uppercase tracking-wide border-l border-neutral-200 ${
                            spec === "area" ? "bg-[#FF7A00] text-white" : "bg-white text-neutral-600"
                          }`}
                          onClick={() => setLineSpecType(lineKey, "area")}
                        >
                          Area
                        </button>
                      </div>
                    </TableCell>
                    <TableCell className="py-3 align-middle">
                      {spec === "quantity" ? (
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <button
                            type="button"
                            data-testid={`${testIdPrefix}-qty-minus-${lineKey}`}
                            onClick={() => applyQuantityDelta(lineKey, -1)}
                            className="w-9 h-9 flex items-center justify-center border border-neutral-200 rounded-lg bg-white hover:bg-neutral-50"
                          >
                            <Minus size={14} weight="bold" />
                          </button>
                          <Input
                            data-testid={`${testIdPrefix}-qty-input-${lineKey}`}
                            type="text"
                            inputMode="numeric"
                            className="h-9 w-14 text-center rounded-lg border-neutral-200 font-mono text-sm"
                            value={quantityDrafts[lineKey] !== undefined ? quantityDrafts[lineKey] : String(p.quantity ?? 1)}
                            onFocus={() =>
                              setQuantityDrafts((prev) => ({ ...prev, [lineKey]: String(p.quantity ?? 1) }))
                            }
                            onChange={(e) => {
                              const raw = e.target.value;
                              if (raw === "" || /^\d+$/.test(raw)) {
                                setQuantityDrafts((prev) => ({ ...prev, [lineKey]: raw }));
                              }
                            }}
                            onBlur={(e) => {
                              const raw = e.target.value.trim();
                              clearQuantityDraft(lineKey);
                              if (raw === "") {
                                toast.error("Quantity must be at least 1");
                                return;
                              }
                              const n = parseInt(raw, 10);
                              if (Number.isNaN(n) || n < 1) {
                                toast.error("Quantity must be at least 1");
                                return;
                              }
                              commitProductQuantity(lineKey, n);
                            }}
                          />
                          <button
                            type="button"
                            data-testid={`${testIdPrefix}-qty-plus-${lineKey}`}
                            onClick={() => applyQuantityDelta(lineKey, 1)}
                            className="w-9 h-9 flex items-center justify-center border border-neutral-200 rounded-lg bg-white hover:bg-neutral-50"
                          >
                            <Plus size={14} weight="bold" />
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 flex-wrap">
                          <Input
                            data-testid={`${testIdPrefix}-area-input-${lineKey}`}
                            type="text"
                            inputMode="decimal"
                            placeholder="Area"
                            className="h-9 w-24 rounded-lg border-neutral-200 text-sm font-mono"
                            value={p.areaValue ?? ""}
                            onChange={(e) => {
                              let v = e.target.value.replace(/[^\d.]/g, "");
                              const dot = v.indexOf(".");
                              if (dot !== -1) {
                                v = v.slice(0, dot + 1) + v.slice(dot + 1).replace(/\./g, "");
                              }
                              setSelectedProducts((prev) => {
                                const row = prev[lineKey];
                                if (!row) return prev;
                                return { ...prev, [lineKey]: { ...row, areaValue: v } };
                              });
                            }}
                          />
                          <Select
                            value={p.areaUnit === "sqm" ? "sqm" : "sqft"}
                            onValueChange={(v) =>
                              setSelectedProducts((prev) => {
                                const row = prev[lineKey];
                                if (!row) return prev;
                                return { ...prev, [lineKey]: { ...row, areaUnit: v } };
                              })
                            }
                          >
                            <SelectTrigger className="h-9 w-[88px] rounded-lg border-neutral-200 text-xs">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="sqft">sq ft</SelectItem>
                              <SelectItem value="sqm">m²</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="py-3 align-middle text-xs font-medium text-neutral-600 whitespace-nowrap">
                      {spec === "quantity" ? productUnitLabel(p) : p.areaUnit === "sqm" ? "m²" : "sq ft"}
                    </TableCell>
                    <TableCell className="py-3 pr-4 text-right align-middle">
                      <button
                        type="button"
                        data-testid={`${testIdPrefix}-remove-${lineKey}`}
                        onClick={() => removeLine(lineKey)}
                        className="inline-flex text-neutral-400 hover:text-red-600 p-2 rounded-lg hover:bg-red-50"
                        aria-label="Remove line"
                      >
                        <Trash size={20} weight="bold" />
                      </button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}
